// Import different image sizes
import heroImageSmall from '../../assets/images/hero-bg-small.png' // ~600px wide for mobile
import heroImageMedium from '../../assets/images/hero-bg-medium.png' // ~1200px wide for tablet
import heroImageLarge from '../../assets/images/hero-bg.png' // Original full size

import React, { useEffect, useState } from 'react'

const Hero = () => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [heroImageSrc, setHeroImageSrc] = useState('')
  const [scrollY, setScrollY] = useState(0)

  // Handle scroll for parallax effects
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Calculate transforms based on scroll
  const backgroundY = scrollY * 0.25
  const contentY = scrollY * 0.3
  const contentOpacity = Math.max(0.3, 1 - scrollY / 500)

  // Select appropriate image based on screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      const pixelRatio = window.devicePixelRatio || 1

      if (width < 768) {
        setHeroImageSrc(heroImageSmall)
      } else if (width < 1280 || pixelRatio === 1) {
        setHeroImageSrc(heroImageMedium)
      } else {
        setHeroImageSrc(heroImageLarge)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Preload image
  useEffect(() => {
    if (!heroImageSrc) return

    const img = new Image()
    img.src = heroImageSrc

    const handleLoad = () => {
      setImageLoaded(true)
      setImageError(false)
    }

    const handleError = () => {
      console.error('Failed to load hero image:', heroImageSrc)
      setImageError(true)
      setImageLoaded(true)
    }

    img.onload = handleLoad
    img.onerror = handleError

    if (img.complete) {
      handleLoad()
    }

    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [heroImageSrc])

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const offset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  // Loading state
  const renderLoading = () => (
    <div className="absolute inset-0 bg-gradient-to-br from-[#596856] to-[#A8B8A3] flex items-center justify-center">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-white rounded-full border-t-transparent animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center text-white font-serif">
          <span className="animate-pulse">Loading</span>
        </div>
      </div>
    </div>
  )

  const titleWords = ['Transform', 'Clutter', 'into', 'Clarity']

  return (
    <section
      id="hero"
      className="relative min-h-[650px] sm:min-h-[700px] md:min-h-screen flex items-center justify-center overflow-hidden bg-[#596856]"
      role="banner"
      aria-label="Hero section"
    >
      {/* Loading State */}
      {!imageLoaded && renderLoading()}

      {/* Background Image with Subtle Opacity for Balance */}
      <div
        className={`absolute inset-0 w-full h-[120%] bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
          imageLoaded ? 'opacity-75' : 'opacity-0'
        }`}
        style={{
          backgroundImage: imageError
            ? 'linear-gradient(135deg, rgba(89,104,86,0.2) 0%, rgba(158,190,165,0.1) 50%, rgba(228,240,228,0.05) 100%)'
            : `url(${heroImageSrc})`,
          transform: `translateY(${backgroundY}px) scale(1)`,
          backgroundPosition: 'center',
          // 75% opacity for good visibility while maintaining text contrast
        }}
        aria-hidden="true"
      >
        {/* Subtle noise texture for depth */}
        <div className="absolute inset-0 opacity-[0.02] mix-blend-multiply pointer-events-none">
          <svg width="100%" height="100%">
            <filter id="noise">
              <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" />
            </filter>
            <rect width="100%" height="100%" filter="url(#noise)" />
          </svg>
        </div>
      </div>

      {/* Strategic Gradient Overlays - Balanced for 75% opacity image */}
      <div className="absolute inset-0 z-10">
        {/* Gentle dark scrim for better text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />

        {/* Soft vignette for edge definition */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.25) 100%)',
          }}
        />
      </div>

      {/* Content Container with enhanced contrast */}
      <div
        className="relative z-20 text-center px-6 max-w-6xl mx-auto"
        style={{
          opacity: contentOpacity,
          transform: `translateY(${contentY}px)`,
        }}
      >
        {/* Content backdrop for readability */}
        <div className="absolute inset-0 -m-8 bg-gradient-to-b from-black/30 via-black/40 to-black/30 blur-3xl rounded-full"></div>

        <div className="relative">
          {/* Animated Badge with balanced backdrop */}
          <div className="inline-block mb-6 sm:mb-8">
            <span className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-white/15 backdrop-blur-lg rounded-full text-white text-xs sm:text-sm font-medium border border-white/25 shadow-2xl">
              <span className="relative flex h-2 sm:h-3 w-2 sm:w-3 mr-2 sm:mr-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D5B8A8] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 sm:h-3 w-2 sm:w-3 bg-[#D5B8A8]"></span>
              </span>
              Now Serving Twin Cities Metro
            </span>
          </div>

          {/* Title with balanced text shadow for readability */}
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif text-white mb-6 sm:mb-8 leading-tight px-4"
            style={{
              textShadow: '2px 2px 4px rgba(0,0,0,0.6), 0 0 15px rgba(0,0,0,0.4)',
            }}
          >
            <span className="sm:hidden">Transform Clutter into Clarity</span>
            <span className="hidden sm:block">
              {titleWords.map((word, index) => (
                <span
                  key={word}
                  className="inline-block mr-2 md:mr-4 relative hover:text-[#D5B8A8] transition-colors duration-200"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${0.2 + index * 0.1}s both`,
                  }}
                >
                  {word}
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D5B8A8]/70 rounded-full scale-x-0 hover:scale-x-100 transition-transform duration-300" />
                </span>
              ))}
            </span>
          </h1>

          {/* Subtitle with balanced contrast */}
          <p
            className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-5 sm:px-6"
            style={{
              textShadow: '1px 1px 3px rgba(0,0,0,0.6), 0 0 10px rgba(0,0,0,0.3)',
            }}
          >
            <span className="leading-snug sm:leading-relaxed inline-block">
              Because every space deserves clarity and calm.
            </span>
            <span className="block mt-2 sm:mt-3 text-[#D5B8A8] font-medium">
              Let's create spaces that inspire productivity and peace.
            </span>
          </p>

          {/* CTA Buttons with strong contrast */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-center items-center px-5 sm:px-0">
            <button
              onClick={() => scrollToSection('contact')}
              className="group relative w-full sm:w-auto inline-flex items-center justify-center px-6 sm:px-10 py-3.5 sm:py-4 
                       bg-[#D5B8A8] hover:bg-[#c5a798] text-white font-semibold rounded-lg overflow-hidden 
                       shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300
                       focus:outline-none focus:ring-4 focus:ring-[#D5B8A8]/50"
              aria-label="Book a consultation"
            >
              <span className="relative z-10 flex items-center justify-center text-base sm:text-lg">
                Book a Consultation
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 ml-2 sm:ml-3 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>
              {/* Animated shine effect */}
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"
                aria-hidden="true"
              />
            </button>

            <button
              onClick={() => scrollToSection('services')}
              className="group relative w-full sm:w-auto inline-flex items-center justify-center px-6 sm:px-10 py-3.5 sm:py-4 
                       text-white font-semibold rounded-lg overflow-hidden 
                       bg-white/10 backdrop-blur-md border-2 border-white/30 hover:border-white/50 
                       hover:bg-white/20 shadow-2xl hover:shadow-3xl transform hover:scale-105 
                       transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-white/30"
              aria-label="View our services"
            >
              <span className="relative z-10 text-base sm:text-lg flex items-center">
                View Services
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </span>
            </button>
          </div>

          {/* Trust Indicators with glass morphism */}
          <div className="mt-12 sm:mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto px-4 sm:px-6">
            {[
              { icon: '⭐', value: '5.0', label: 'Client Rating', color: '#D5B8A8' },
              { icon: '👥', value: '50+', label: 'Happy Clients', color: '#B0A0B9' },
              { icon: '🎓', value: '10+', label: 'Years Experience', color: '#A8B8A3' },
            ].map((item, index) => (
              <div
                key={item.label}
                className="group flex flex-col items-center text-white backdrop-blur-md bg-white/10 
                         rounded-xl p-3 sm:p-4 md:p-5 border border-white/20 
                         hover:bg-white/20 transform hover:-translate-y-1 
                         transition-all duration-300 shadow-xl hover:shadow-2xl"
              >
                <div
                  className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 backdrop-blur-md rounded-full 
                           flex items-center justify-center mb-2 sm:mb-3 text-xl sm:text-2xl md:text-3xl"
                  style={{
                    backgroundColor: `${item.color}20`,
                    borderColor: `${item.color}40`,
                    borderWidth: '1px',
                  }}
                >
                  <span>{item.icon}</span>
                </div>
                <span className="text-xl sm:text-2xl md:text-3xl font-bold mb-0.5 sm:mb-1">
                  {item.value}
                </span>
                <span className="text-xs sm:text-sm opacity-90">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Subtle animated decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <div className="absolute -top-32 -right-32 w-[300px] h-[300px] bg-[#D5B8A8]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-32 -left-32 w-[300px] h-[300px] bg-[#B0A0B9]/10 rounded-full blur-3xl animate-pulse animation-delay-1000" />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-white/70 text-xs z-20">
        <span className="mb-2">Scroll to explore</span>
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1">
          <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" />
        </div>
      </div>

      {/* Internal styles for animations */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animation-delay-1000 {
            animation-delay: 1s;
          }
        `,
        }}
      />
    </section>
  )
}

export default Hero
